import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import {
  AudienceApiService,
  AudienceVolumeTrendPoint,
  AudienceExportVolume
} from '../../../../services/audience-api.service';
import { AudienceVolumesTrendChartComponent } from '../../volumes-trend-chart/audience-volumes-trend-chart.component';

interface ExportBox {
  key: string;
  name: string;
  entity: string | null;
  volume: number;
}

const DEFAULT_VISIBLE = 8;

/**
 * Volumes tab of the advanced "Additional Info" card: the daily volumes-trend bar chart (New/Existing/
 * Removed, ~14 days) plus one info-box per Export ID Type. Loads both series when the audience changes.
 */
@Component({
  selector: 'app-audience-volumes-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule, AudienceVolumesTrendChartComponent],
  templateUrl: './audience-volumes-tab.component.html',
  styleUrls: ['./audience-volumes-tab.component.scss']
})
export class AudienceVolumesTabComponent implements OnChanges {
  @Input() audienceId: string | null = null;

  trend: AudienceVolumeTrendPoint[] = [];
  exportBoxes: ExportBox[] = [];
  loading = false;
  error = false;
  showAll = false;

  private loadedFor: string | null = null;

  constructor(private audienceApi: AudienceApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['audienceId'] && this.audienceId && this.audienceId !== this.loadedFor) {
      this.load(this.audienceId);
    }
  }

  private load(audienceId: string): void {
    this.loadedFor = audienceId;
    this.loading = true;
    this.error = false;
    this.showAll = false;
    forkJoin({
      trend: this.audienceApi.getAudienceVolumeTrend(audienceId),
      exports: this.audienceApi.getAudienceExportVolumes(audienceId)
    }).subscribe({
      next: ({ trend, exports }) => {
        if (this.loadedFor !== audienceId) {
          return;
        }
        this.trend = trend;
        this.exportBoxes = this.mapExports(exports);
        this.loading = false;
      },
      error: () => {
        if (this.loadedFor !== audienceId) {
          return;
        }
        this.trend = [];
        this.exportBoxes = [];
        this.error = true;
        this.loading = false;
      }
    });
  }

  private mapExports(rows: AudienceExportVolume[]): ExportBox[] {
    return (rows ?? [])
      .map((r, i) => ({
        key: `${r.ExportIDType}__${r.EntityID ?? ''}__${i}`,
        name: r.ExportIDName || r.ExportIDType,
        entity: r.EntityName || null,
        volume: Number(r.Volumes) || 0
      }))
      .sort((a, b) => b.volume - a.volume);
  }

  get visibleBoxes(): ExportBox[] {
    return this.showAll ? this.exportBoxes : this.exportBoxes.slice(0, DEFAULT_VISIBLE);
  }

  get hasMore(): boolean {
    return this.exportBoxes.length > DEFAULT_VISIBLE;
  }

  toggleShowAll(): void {
    this.showAll = !this.showAll;
  }

  get hasData(): boolean {
    return this.trend.some(t => Number(t.Volumes) > 0) || this.exportBoxes.length > 0;
  }

  formatNumber(value: number): string {
    return value.toLocaleString('it-IT');
  }

  trackByKey(_index: number, box: ExportBox): string {
    return box.key;
  }
}
